import React, { createContext, useContext, useState, useEffect } from 'react';
import { auth, db } from '../firebase'; // Ensure this path is correct
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
            if (firebaseUser) {
                const userDocRef = doc(db, 'users', firebaseUser.uid);
                const userDoc = await getDoc(userDocRef);

                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    const userRoles = userData.roles || [];
                    let userPermissions = [];

                    if (userRoles.length > 0) {
                        const permissionPromises = userRoles.map(roleName => 
                            getDoc(doc(db, 'roles', roleName))
                        );
                        const permissionDocs = await Promise.all(permissionPromises);
                        
                        const permissionsSet = new Set();
                        permissionDocs.forEach(doc => {
                            if (doc.exists()) {
                                doc.data().permissions.forEach(p => permissionsSet.add(p));
                            }
                        });
                        userPermissions = [...permissionsSet];
                    }

                    const appUser = {
                        uid: firebaseUser.uid,
                        email: firebaseUser.email,
                        ...userData,
                        permissions: userPermissions,
                    };
                    
                    setUser(appUser);
                    localStorage.setItem('user', JSON.stringify(appUser));
                } else {
                    console.error("User document not found in Firestore for UID:", firebaseUser.uid);
                    setUser(null); // Log out user if their DB record is missing
                    localStorage.removeItem('user');
                }
            } else {
                setUser(null);
                localStorage.removeItem('user');
            }
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    const login = async (email, password) => {
        try {
            await signInWithEmailAndPassword(auth, email, password);
            return true;
        } catch (error) {
            console.error("Firebase login failed:", error.message);
            return false;
        }
    };

    const logout = async () => {
        await signOut(auth);
    };

    const hasPermission = (requiredPermission) => {
        return user?.permissions?.includes(requiredPermission);
    };
    
    const value = {
        user,
        login,
        logout,
        hasPermission 
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    return useContext(AuthContext);
};